import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Table from '../../components/Listing/Table';
import { usePageTitle } from '../../layouts/PageTitleContext';
import { API_BASE_URL } from '../../api';

const PLAN_LABELS = { MONTHLY: 'Mensuel', YEARLY: 'Annuel' };

const STATUS_STYLES = {
    ACTIVE:  { label: 'Actif',   bg: '#ecfdf5', color: '#065f46', dot: '#10b981' },
    TRIAL:   { label: 'Essai',   bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6' },
    PENDING: { label: 'En attente', bg: '#fff7ed', color: '#9a3412', dot: '#f97316' },
    EXPIRED: { label: 'Expiré',  bg: '#fef2f2', color: '#991b1b', dot: '#ef4444' },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

export default function BackofficePharmacies() {
    const { setTitle, setTitleIcon, setTitleDescription } = usePageTitle();
    const [pharmacies, setPharmacies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keyword, setKeyword] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        return axios.get(`${API_BASE_URL}/api/v1/backoffice/pharmacies`, { withCredentials: true })
            .then(({ data }) => setPharmacies(data))
            .catch(() => toast.error('Impossible de charger les pharmacies.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        document.title = 'Backoffice — Pharmacies';
        load();
    }, [load]);

    useEffect(() => {
        setTitle('Pharmacies');
        setTitleDescription('Pharmacies ayant créé un compte sur la plateforme');
        setTitleIcon('ti-building-store');
        return () => { setTitleDescription(null); setTitleIcon(null); };
    }, [setTitle, setTitleDescription, setTitleIcon]);

    const columns = [
        { accessor: 'name', Header: 'Pharmacie', sortable: true, Cell: (row) => <span style={{ fontWeight: 600 }}>{row.name}</span> },
        { accessor: 'ownerName', Header: 'Propriétaire', Cell: (row) => row.ownerName || '—' },
        { accessor: 'email', Header: 'Email' },
        { accessor: 'city', Header: 'Ville', Cell: (row) => row.city || '—' },
        { accessor: 'creationDate', Header: 'Créée le', sortable: true, Cell: (row) => fmtDate(row.creationDate) },
        {
            accessor: 'subscriptionPlan',
            Header: 'Plan',
            Cell: (row) => row.subscriptionPlan ? (PLAN_LABELS[row.subscriptionPlan] || row.subscriptionPlan) : '—',
        },
        {
            accessor: 'subscriptionStatus',
            Header: 'Abonnement',
            Cell: (row) => {
                const cfg = STATUS_STYLES[row.subscriptionStatus];
                if (!cfg) return <span className="text-muted">—</span>;
                return (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: cfg.bg, color: cfg.color,
                        padding: '3px 9px', borderRadius: 999,
                        fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                        {cfg.label}
                    </span>
                );
            },
        },
    ];

    return (
        <Table
            columns={columns}
            data={pharmacies}
            mode="client"
            loading={loading}
            keyword={keyword}
            setKeyword={setKeyword}
            searchPlaceholder="Rechercher une pharmacie..."
            rowId="id"
            pageSize={20}
            emptyMessage="Aucune pharmacie n'a encore créé de compte"
        />
    );
}
